/* ==========================================================================
   PLESSA Editorial — theme.js
   Vanilla JS, no dependencies. Custom elements re-initialise themselves when
   the theme editor re-renders a section.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const fetchHTML = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return parseHTML(await response.text());
  };

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        /* storage unavailable (private mode) */
      }
    },
  };

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* ------------------------------------------------------------------------
     Dialogs: drawers, search, popup and lightbox all share this controller
     ------------------------------------------------------------------------ */
  const openDialogs = new Set();
  const CLOSE_MS = 420;

  const syncLock = () => {
    document.documentElement.classList.toggle('is-locked', openDialogs.size > 0);
  };

  const setExpanded = (dialog, expanded) => {
    if (!dialog.id) return;
    $$(`[aria-controls="${dialog.id}"]`).forEach((control) => {
      if (control.hasAttribute('aria-expanded')) control.setAttribute('aria-expanded', String(expanded));
    });
  };

  const Dialog = {
    open(dialog, opener) {
      if (!dialog) return;
      if (dialog.open && !dialog._closing) return;
      openDialogs.forEach((other) => {
        if (other !== dialog) Dialog.close(other, { restoreFocus: false });
      });
      clearTimeout(dialog._closeTimer);
      dialog._closing = false;
      dialog._opener = opener || document.activeElement;
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }
      openDialogs.add(dialog);
      syncLock();
      setExpanded(dialog, true);
      const reveal = () => dialog.classList.add('is-open');
      requestAnimationFrame(() => requestAnimationFrame(reveal));
      setTimeout(reveal, 80);
      dialog.dispatchEvent(new CustomEvent('dialog:open'));
    },

    close(dialog, { restoreFocus = true } = {}) {
      if (!dialog || !dialog.open || dialog._closing) return;
      dialog._closing = true;
      dialog.classList.remove('is-open');
      setExpanded(dialog, false);
      const finish = () => {
        dialog._closing = false;
        if (dialog.open) {
          if (typeof dialog.close === 'function') dialog.close();
          else dialog.removeAttribute('open');
        }
        openDialogs.delete(dialog);
        syncLock();
        const opener = dialog._opener;
        if (restoreFocus && opener && document.contains(opener) && typeof opener.focus === 'function') {
          opener.focus({ preventScroll: true });
        }
        dialog.dispatchEvent(new CustomEvent('dialog:close'));
      };
      dialog._closeTimer = setTimeout(finish, reducedMotion.matches ? 0 : CLOSE_MS);
    },
  };

  let pointerDownTarget = null;
  document.addEventListener('pointerdown', (event) => { pointerDownTarget = event.target; }, true);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.open);
      if (target instanceof HTMLDialogElement) {
        event.preventDefault();
        Dialog.open(target, opener);
        return;
      }
    }

    const closer = event.target.closest('[data-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) {
        event.preventDefault();
        Dialog.close(dialog);
      }
      return;
    }

    // A click on the dimmed area lands on the <dialog> itself.
    const target = event.target;
    if (target instanceof HTMLDialogElement && target.open && pointerDownTarget === target) {
      Dialog.close(target);
    }
  });

  // Esc: animate out instead of closing instantly.
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target instanceof HTMLDialogElement) {
        event.preventDefault();
        Dialog.close(event.target);
      }
    },
    true
  );

  // If the browser force-closes a dialog, keep our state in sync.
  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || dialog._closing) return;
      if (openDialogs.has(dialog)) {
        dialog.classList.remove('is-open');
        openDialogs.delete(dialog);
        setExpanded(dialog, false);
        syncLock();
      }
    },
    true
  );

  /* ------------------------------------------------------------------------
     Header: height variables, transparent-over-hero, hide on scroll
     ------------------------------------------------------------------------ */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header');
      this.announcement = document.querySelector('.section-announcement');
      this.transparent = this.dataset.transparent === 'true';
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;

      this.measure = this.measure.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this);
      if (this.announcement) this.resizeObserver.observe(this.announcement);
      this.measure();

      if (this.transparent) this.watchHero();
      if (this.hideOnScroll) window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.heroObserver) this.heroObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    measure() {
      const style = document.documentElement.style;
      style.setProperty('--header-h', `${Math.round(this.getBoundingClientRect().height)}px`);
      const annHeight = this.announcement ? Math.round(this.announcement.getBoundingClientRect().height) : 0;
      style.setProperty('--ann-h', `${annHeight}px`);
      if (this.transparent && this.heroObserver) this.watchHero();
    }

    watchHero() {
      const hero = document.querySelector('#MainContent > .shopify-section:first-child');
      if (this.heroObserver) this.heroObserver.disconnect();
      if (!hero) {
        this.classList.add('is-solid');
        return;
      }
      const offset = Math.round(this.getBoundingClientRect().height) + 1;
      this.heroObserver = new IntersectionObserver(
        ([entry]) => this.classList.toggle('is-solid', !entry.isIntersecting),
        { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 }
      );
      this.heroObserver.observe(hero);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        const y = window.scrollY;
        const delta = y - this.lastY;
        if (Math.abs(delta) < 6) return;
        const locked = document.documentElement.classList.contains('is-locked');
        const hide = delta > 0 && y > Math.max(420, window.innerHeight * 0.6) && !locked;
        if (this.section) this.section.classList.toggle('is-hidden', hide);
        document.documentElement.style.setProperty('--sticky-top', hide ? '0px' : 'var(--header-h)');
        this.lastY = y;
      });
    }
  }
  define('site-header', SiteHeader);

  /* ------------------------------------------------------------------------
     Marquee: fills each group to the viewport and keeps a constant speed
     ------------------------------------------------------------------------ */
  class MarqueeBar extends HTMLElement {
    connectedCallback() {
      this.groups = $$('.ann__group', this);
      if (this.groups.length < 2) return;
      this.update = this.update.bind(this);
      this.fill();
      this.resizeObserver = new ResizeObserver(debounce(() => {
        this.fill();
        this.update();
      }, 150));
      this.resizeObserver.observe(this);
      this.update();

      const toggle = $("[data-marquee-toggle]", this);
      if (toggle) {
        toggle.addEventListener("click", () => {
          const paused = this.classList.toggle("is-paused");
          toggle.setAttribute("aria-label", paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
        });
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    fill() {
      const [first] = this.groups;
      if (!first.dataset.original) first.dataset.original = String(first.children.length);
      const originals = Array.from(first.children).slice(0, Number(first.dataset.original));
      let guard = 0;
      while (first.scrollWidth < window.innerWidth && guard < 12) {
        this.groups.forEach((group, index) => {
          originals.forEach((item) => {
            const clone = item.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.removeAttribute('data-shopify-editor-block');
            $$('a', clone).forEach((link) => link.setAttribute('tabindex', '-1'));
            group.appendChild(clone);
          });
          if (index === 0) group.dataset.filled = 'true';
        });
        guard += 1;
      }
    }

    update() {
      const speed = Number(this.dataset.speed) || 50;
      const width = this.groups[0].scrollWidth;
      this.style.setProperty('--marquee-duration', `${Math.max(width / speed, 6).toFixed(2)}s`);
    }
  }
  define('marquee-bar', MarqueeBar);

  /* ------------------------------------------------------------------------
     Fit text: scales a single line to the width of its parent
     ------------------------------------------------------------------------ */
  class FitText extends HTMLElement {
    connectedCallback() {
      if (this.dataset.fit === 'false') return;
      this.fit = this.fit.bind(this);
      this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.fit));
      if (this.parentElement) this.resizeObserver.observe(this.parentElement);
      this.fit();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    fit() {
      const parent = this.parentElement;
      if (!parent) return;
      const styles = getComputedStyle(parent);
      const available = parent.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
      if (available <= 0) return;
      this.style.setProperty('--fit-size', '100px');
      const width = this.getBoundingClientRect().width;
      if (!width) return;
      const size = Math.min((available / width) * 100 * 0.995, 1600);
      this.style.setProperty('--fit-size', `${size.toFixed(2)}px`);
    }
  }
  define('fit-text', FitText);

  /* ------------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------------ */
  const revealEnabled =
    document.body.classList.contains('has-reveal') &&
    'IntersectionObserver' in window &&
    !reducedMotion.matches &&
    !designMode;

  const revealObserver = revealEnabled
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.01 }
      )
    : null;

  const initReveal = (root = document) => {
    const elements = $$('[data-reveal]:not(.is-in)', root);
    if (!revealObserver) {
      elements.forEach((element) => element.classList.add('is-in'));
      return;
    }
    $$('.grid, .cats__grid, .lb__grid', root).forEach((grid) => {
      Array.from(grid.children).forEach((child, index) => {
        const element = child.matches('[data-reveal]') ? child : child.querySelector('[data-reveal]');
        if (element && !element.style.getPropertyValue('--i')) element.style.setProperty('--i', String(index % 4));
      });
    });
    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0 && !document.documentElement.classList.contains('reveal-ready')) {
        element.classList.add('is-in');
      } else {
        revealObserver.observe(element);
      }
    });
  };

  /* ------------------------------------------------------------------------
     Snap carousel: arrows, drag to scroll, progress and slide counter
     ------------------------------------------------------------------------ */
  class SnapCarousel extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-track]', this);
      if (!this.track) return;
      this.prevButton = $('[data-prev]', this);
      this.nextButton = $('[data-next]', this);
      this.progress = $('[data-progress]', this);
      this.current = $('[data-current]', this);

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.go(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.go(1));
      if (!this.hasAttribute('data-mobile-only')) this.enableDrag();
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onScroll);
      if (this.onPointerMove) window.removeEventListener('pointermove', this.onPointerMove);
      if (this.onPointerUp) window.removeEventListener('pointerup', this.onPointerUp);
    }

    go(direction) {
      this.glide(this.track.scrollLeft + this.track.clientWidth * 0.75 * direction);
    }

    // Manual glide: smooth scrollTo is unreliable on scroll-snap containers.
    glide(target) {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const end = Math.max(0, Math.min(max, target));
      const start = track.scrollLeft;
      const distance = end - start;
      if (Math.abs(distance) < 1) return;
      if (reducedMotion.matches) {
        track.scrollLeft = end;
        return;
      }
      cancelAnimationFrame(this.frame);
      const snap = track.style.scrollSnapType;
      track.style.scrollSnapType = 'none';
      const began = performance.now();
      const duration = 650;
      const step = (now) => {
        const progress = Math.min(1, (now - began) / duration);
        track.scrollLeft = start + distance * (1 - Math.pow(1 - progress, 3));
        if (progress < 1) this.frame = requestAnimationFrame(step);
        else track.style.scrollSnapType = snap;
      };
      this.frame = requestAnimationFrame(step);
    }

    onScroll() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const ratio = max > 0 ? track.scrollLeft / max : 1;
      if (this.progress) {
        const visible = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
        this.progress.style.setProperty('--progress', String(Math.min(1, visible + (1 - visible) * ratio)));
      }
      if (this.prevButton) this.prevButton.disabled = track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = track.scrollLeft >= max - 2;
      if (this.current && track.clientWidth) {
        this.current.textContent = String(Math.round(track.scrollLeft / track.clientWidth) + 1);
      }
    }

    enableDrag() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const track = this.track;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      });

      this.onPointerMove = (event) => {
        if (!dragging) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) track.scrollLeft = startScroll - dx;
      };

      this.onPointerUp = () => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        track.classList.remove('is-dragging');
        this.suppressClick = true;
        setTimeout(() => { this.suppressClick = false; }, 60);
      };

      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      track.addEventListener('dragstart', (event) => event.preventDefault());
      track.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }
  }
  define('snap-carousel', SnapCarousel);

  /* ------------------------------------------------------------------------
     Cart (AJAX + Section Rendering API)
     ------------------------------------------------------------------------ */
  const Cart = {
    sectionId: 'cart-drawer',

    get drawer() {
      return document.getElementById('CartDrawer');
    },

    async request(url, body, isJSON) {
      const response = await fetch(url, {
        method: 'POST',
        headers: isJSON
          ? { 'Content-Type': 'application/json', Accept: 'application/json' }
          : { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.error);
      }
      return data;
    },

    async add(formData) {
      if (this.drawer) {
        formData.append('sections', this.sectionId);
        formData.append('sections_url', window.location.pathname);
      }
      const data = await this.request(`${theme.routes.cartAdd}.js`, formData, false);
      await this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'add', item: data } }));
      return data;
    },

    async change(key, quantity) {
      const body = { id: key, quantity };
      if (this.drawer) {
        body.sections = [this.sectionId];
        body.sections_url = window.location.pathname;
      }
      const data = await this.request(`${theme.routes.cartChange}.js`, JSON.stringify(body), true);
      await this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'change', cart: data } }));
      return data;
    },

    async render(sections) {
      const html = sections && sections[this.sectionId];
      if (!html) {
        await this.refreshCount();
        return;
      }
      const doc = parseHTML(html);
      const fresh = $('[data-cart-drawer-inner]', doc);
      const current = $('[data-cart-drawer-inner]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      const count = $('[data-cart-count]', doc);
      if (count) this.setCount(count.textContent.trim());
    },

    async refreshCount() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        this.setCount(String(cart.item_count));
      } catch (error) {
        /* ignore */
      }
    },

    setCount(value) {
      $$('[data-cart-count]').forEach((element) => {
        element.textContent = value;
      });
    },
  };

  const showFormError = (form, message) => {
    let target = $('[data-form-error]', form);
    if (!target) {
      target = document.createElement('p');
      target.className = 'form-error';
      target.setAttribute('role', 'alert');
      target.setAttribute('data-form-error', '');
      form.appendChild(target);
    }
    target.textContent = message;
    target.hidden = false;
  };

  // Every product form (product page, quick add) posts through here.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    if (theme.cartType !== 'drawer' || !Cart.drawer) return;

    event.preventDefault();
    const submitter = event.submitter || $('[type="submit"]', form);
    const formData = new FormData(form);
    if (submitter && submitter.name === 'id' && submitter.value) formData.set('id', submitter.value);

    if (!formData.get('id')) {
      form.dispatchEvent(new CustomEvent('product:needs-option', { bubbles: true }));
      return;
    }

    const error = $('[data-form-error]', form);
    if (error) error.hidden = true;
    if (submitter) {
      submitter.classList.add('is-loading');
      submitter.setAttribute('aria-busy', 'true');
    }

    try {
      await Cart.add(formData);
      const quickAdd = form.closest('quick-add');
      if (quickAdd && typeof quickAdd.close === 'function') quickAdd.close();
      Dialog.open(Cart.drawer, submitter);
    } catch (err) {
      showFormError(form, err.message);
    } finally {
      if (submitter) {
        submitter.classList.remove('is-loading');
        submitter.removeAttribute('aria-busy');
      }
    }
  });

  /* ------------------------------------------------------------------------
     Cart drawer: quantity steppers, remove, order note
     ------------------------------------------------------------------------ */
  class CartDrawerElement extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener(
        'input',
        debounce((event) => {
          if (!event.target.matches('[data-cart-note]')) return;
          fetch(`${theme.routes.cart}/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ note: event.target.value }),
          });
        }, 400)
      );
    }

    async onClick(event) {
      const button = event.target.closest('[data-line-quantity]');
      if (!button) return;
      event.preventDefault();
      const item = button.closest('[data-line-key]');
      if (!item) return;

      const key = item.dataset.lineKey;
      const label = button.getAttribute('aria-label');
      item.classList.add('is-updating');

      try {
        await Cart.change(key, Number(button.dataset.lineQuantity));
        const sameItem = $(`[data-line-key="${CSS.escape(key)}"]`, this);
        const sameButton = sameItem && label ? $(`[aria-label="${CSS.escape(label)}"]`, sameItem) : null;
        const fallback = $('.drawer__close', this);
        (sameButton || fallback)?.focus({ preventScroll: true });
      } catch (err) {
        item.classList.remove('is-updating');
        const message = $('[data-cart-error]', this);
        if (message) {
          message.textContent = err.message;
          message.hidden = false;
        }
      }
    }
  }
  define('cart-drawer', CartDrawerElement);

  /* ------------------------------------------------------------------------
     Quick add tray on product cards
     ------------------------------------------------------------------------ */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.toggle = $('.qa__toggle[aria-controls]', this);
      this.tray = $('.qa__tray', this);
      if (!this.toggle || !this.tray) return;

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        this.close();
        this.toggle.focus();
      };

      this.toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.tray.hidden) this.open();
        else this.close();
      });
    }

    disconnectedCallback() {
      this.close();
    }

    open() {
      $$('quick-add.is-open').forEach((other) => {
        if (other !== this) other.close();
      });
      this.tray.hidden = false;
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);

      const rect = this.tray.getBoundingClientRect();
      if (rect.left < 8) this.tray.style.right = `${rect.left - 8}px`;
      const first = $('button:not(:disabled)', this.tray);
      if (first) first.focus({ preventScroll: true });
    }

    close() {
      if (!this.tray || this.tray.hidden) return;
      this.tray.hidden = true;
      this.tray.style.right = '';
      this.classList.remove('is-open');
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
    }
  }
  define('quick-add', QuickAdd);

  /* ------------------------------------------------------------------------
     Variant picker (size boxes)
     ------------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = $('[data-variants]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.formId = this.dataset.formId;
      this.sectionId = this.dataset.sectionId;

      this.addEventListener('change', () => this.onChange());
      this.onNeedsOption = (event) => {
        if (event.target && event.target.id === this.formId) this.flash();
      };
      document.addEventListener('product:needs-option', this.onNeedsOption);
      this.updateAvailability();
    }

    disconnectedCallback() {
      document.removeEventListener('product:needs-option', this.onNeedsOption);
    }

    get fieldsets() {
      return $$('fieldset[data-option-index]', this);
    }

    selectedValues() {
      return this.fieldsets.map((fieldset) => {
        const checked = $('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const values = this.selectedValues();
      this.fieldsets.forEach((fieldset, index) => {
        const label = $('[data-selected-value]', fieldset);
        if (label) label.textContent = values[index] || '';
      });
      this.updateAvailability(values);
      if (values.includes(null)) return;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === values[index])
      );
      this.applyVariant(variant);
    }

    updateAvailability(values = this.selectedValues()) {
      this.fieldsets.forEach((fieldset, index) => {
        $$('input', fieldset).forEach((input) => {
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === input.value &&
              values.every((value, other) => other === index || value === null || variant.options[other] === value)
          );
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    applyVariant(variant) {
      const form = document.getElementById(this.formId);
      const button = form ? $('[data-add-button]', form) : null;
      const label = form ? $('[data-add-label]', form) : null;
      const dynamic = form ? $('[data-dynamic-checkout]', form) : null;

      const idInputs = [
        ...(form ? $$('[data-variant-id]', form) : []),
        ...$$(`#${CSS.escape(this.dataset.installmentsId || '')} [data-variant-id]`),
      ];
      idInputs.forEach((input) => {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      if (button && label) {
        if (!variant) {
          button.disabled = true;
          label.textContent = theme.strings.unavailable;
        } else if (!variant.available) {
          button.disabled = true;
          label.textContent = theme.strings.soldOut;
        } else {
          button.disabled = false;
          label.textContent = theme.strings.addToCart;
        }
      }
      if (dynamic) dynamic.classList.toggle('is-hidden', !variant || !variant.available);
      if (form) {
        const error = $('[data-form-error]', form);
        if (error) error.hidden = true;
      }

      if (!variant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());

      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, formId: this.formId } }));
      this.updatePrice(variant);
    }

    async updatePrice(variant) {
      const key = `${variant.price}|${variant.compare_at_price}|${variant.unit_price || ''}`;
      if (this.priceKey === undefined) this.priceKey = key;
      if (this.priceKey === key) return;
      this.priceKey = key;
      try {
        const doc = await fetchHTML(`${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`);
        const fresh = $('[data-product-price]', doc);
        const current = $(`[data-section-id="${CSS.escape(this.sectionId)}"] [data-product-price]`);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        document.dispatchEvent(new CustomEvent('variant:price', { detail: { formId: this.formId } }));
      } catch (error) {
        /* keep the current price */
      }
    }

    flash() {
      this.classList.remove('is-flash');
      void this.offsetWidth;
      this.classList.add('is-flash');
      this.scrollIntoView({ block: 'center', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      const firstInput = $('input:not(:checked)', this);
      if (firstInput) firstInput.focus({ preventScroll: true });
    }
  }
  define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------------
     Sticky add to cart (mobile)
     ------------------------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.mainButton = this.form ? $('[data-add-button]', this.form) : null;
      this.button = $('[data-sticky-add]', this);
      this.price = $('.satc__price', this);
      if (!this.mainButton || !this.button) return;

      this.hidden = false;
      this.syncButton();

      this.observer = new IntersectionObserver(([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
      });
      this.observer.observe(this.mainButton);

      this.button.addEventListener('click', () => {
        const idInput = $('[data-variant-id]', this.form);
        if (!idInput || !idInput.value) {
          const picker = $(`variant-picker[data-form-id="${CSS.escape(this.dataset.formId)}"]`);
          if (picker) picker.flash();
          else this.mainButton.scrollIntoView({ block: 'center' });
          return;
        }
        if (this.mainButton.disabled) return;
        this.button.classList.add('is-loading');
        this.form.requestSubmit(this.mainButton);
      });

      this.onVariant = (event) => {
        if (event.detail.formId === this.dataset.formId) this.syncButton();
      };
      this.onPrice = (event) => {
        if (event.detail.formId !== this.dataset.formId) return;
        const current = $('[data-product-price] .price__current');
        if (current && this.price) this.price.textContent = current.textContent.trim();
      };
      this.onCart = () => this.button.classList.remove('is-loading');

      document.addEventListener('variant:change', this.onVariant);
      document.addEventListener('variant:price', this.onPrice);
      document.addEventListener('cart:updated', this.onCart);
      this.form.addEventListener('product:needs-option', this.onCart);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('variant:change', this.onVariant);
      document.removeEventListener('variant:price', this.onPrice);
      document.removeEventListener('cart:updated', this.onCart);
    }

    syncButton() {
      const idInput = $('[data-variant-id]', this.form);
      const hasVariant = Boolean(idInput && idInput.value);
      const label = $('[data-add-label]', this.form);
      if (hasVariant) {
        this.button.textContent = label ? label.textContent.trim() : theme.strings.addToCart;
        this.button.disabled = this.mainButton.disabled;
      } else {
        this.button.textContent = label ? label.textContent.trim() : theme.strings.selectOption;
        this.button.disabled = false;
      }
      setTimeout(() => this.button.classList.remove('is-loading'), 4000);
    }
  }
  define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------------------------
     Product recommendations (lazy)
     ------------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded || !this.dataset.url) return;
      const load = async () => {
        this.dataset.loaded = 'true';
        try {
          const doc = await fetchHTML(this.dataset.url);
          const fresh = $('product-recommendations', doc);
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            initReveal(this);
          }
        } catch (error) {
          /* recommendations are optional */
        }
      };
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(this);
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------------
     Predictive search
     ------------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-search-results]', this);
      this.dialog = this.closest('dialog');
      if (!this.input || !this.results) return;

      this.enabled = this.dataset.enabled !== 'false';
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.search(), 220));

      if (this.dialog) {
        this.dialog.addEventListener('dialog:open', () => {
          setTimeout(() => this.input.focus({ preventScroll: true }), 80);
        });
      }
    }

    reset() {
      this.results.innerHTML = '';
      if (this.dialog) this.dialog.classList.remove('has-results');
    }

    async search() {
      if (!this.enabled) return;
      const terms = this.input.value.trim();
      if (terms.length < 2) {
        this.reset();
        return;
      }

      const params = new URLSearchParams({
        q: terms,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: this.dataset.sectionId || 'predictive-search',
      });
      const url = `${theme.routes.predictiveSearch}?${params.toString()}`;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.results.classList.add('is-loading');

      try {
        let html = this.cache.get(url);
        if (!html) {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(String(response.status));
          html = await response.text();
          this.cache.set(url, html);
        }
        const fresh = $('[data-predictive-results]', parseHTML(html));
        this.results.innerHTML = fresh ? fresh.outerHTML : '';
        if (this.dialog) this.dialog.classList.toggle('has-results', Boolean(fresh));
      } catch (error) {
        if (error.name !== 'AbortError') this.reset();
      } finally {
        this.results.classList.remove('is-loading');
      }
    }
  }
  define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------------
     Collection & search filters (Section Rendering API)
     ------------------------------------------------------------------------ */
  const Facets = {
    async load(url, sectionId, { scroll = false } = {}) {
      const results = document.getElementById(`FacetResults-${sectionId}`);
      const drawer = document.getElementById(`FacetDrawer-${sectionId}`);
      const target = new URL(url, window.location.origin);
      const fetchUrl = new URL(target.toString());
      fetchUrl.searchParams.set('section_id', sectionId);

      if (results) results.classList.add('is-loading');
      if (drawer) drawer.classList.add('is-loading');

      try {
        const doc = await fetchHTML(fetchUrl.toString());

        const freshResults = doc.getElementById(`FacetResults-${sectionId}`);
        if (freshResults && results) {
          results.innerHTML = freshResults.innerHTML;
          initReveal(results);
        }

        const form = document.getElementById(`FacetForm-${sectionId}`);
        const freshForm = doc.getElementById(`FacetForm-${sectionId}`);
        if (form && freshForm) {
          const active = document.activeElement;
          const focusKey = active && form.contains(active) ? `${active.name}::${active.value}` : null;
          const openStates = $$('details', form).map((details) => details.open);
          form.innerHTML = freshForm.innerHTML;
          $$('details', form).forEach((details, index) => {
            if (openStates[index] !== undefined) details.open = openStates[index];
          });
          if (focusKey) {
            const match = $$('input', form).find((input) => `${input.name}::${input.value}` === focusKey);
            if (match) match.focus({ preventScroll: true });
          }
        }

        [
          '[data-facet-count]',
          `[data-open="FacetDrawer-${sectionId}"]`,
          `#FacetDrawer-${sectionId} .fct__foot`,
        ].forEach((selector) => {
          const fresh = $(selector, doc);
          const current = $(selector);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });

        window.history.replaceState(window.history.state, '', `${target.pathname}${target.search}`);

        if (scroll && results) {
          const top = results.getBoundingClientRect().top + window.scrollY - 120;
          window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        }
      } catch (error) {
        window.location.href = target.toString();
      } finally {
        if (results) results.classList.remove('is-loading');
        if (drawer) drawer.classList.remove('is-loading');
      }
    },
  };

  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', debounce(() => this.submit(), 350));
      this.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
    }

    submit() {
      const form = $('form', this);
      if (!form) return;
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form).entries()) {
        if (value !== '') params.append(key, value);
      }
      const query = params.toString();
      Facets.load(`${window.location.pathname}${query ? `?${query}` : ''}`, this.sectionId);
    }
  }
  define('facet-form', FacetForm);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-facet-link], [data-facet-results] .pagination a');
    if (!link || event.metaKey || event.ctrlKey) return;
    const container = link.closest('[data-facet-results], dialog.fct');
    const sectionId = container ? container.id.replace(/^(FacetResults|FacetDrawer)-/, '') : '';
    if (!sectionId) return;
    event.preventDefault();
    Facets.load(link.href, sectionId, { scroll: Boolean(link.closest('.pagination')) });
  });

  /* ------------------------------------------------------------------------
     New arrivals showcase: auto-cycling product slides with a follow cursor
     ------------------------------------------------------------------------ */
  class NewArrivals extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.na__slide', this);
      this.items = $$('[data-item]', this);
      this.dots = $$('[data-dot]', this);
      this.media = $('[data-media]', this);
      this.cursor = $('[data-cursor]', this);
      this.progress = $('[data-progress]', this);
      if (this.slides.length === 0) return;

      this.index = 0;
      this.count = this.slides.length;
      this.interval = Math.max(2, Number(this.dataset.interval) || 5) * 1000;
      this.canAutoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches && this.count > 1;

      this.bindControls();
      this.bindPointer();
      this.bindVisibility();
      this.bindToggle();
      if (this.canAutoplay) this.play();
    }

    bindToggle() {
      this.toggle = $("[data-na-toggle]", this);
      if (!this.toggle) return;
      if (!this.canAutoplay) {
        this.toggle.hidden = true;
        return;
      }
      this.toggle.addEventListener("click", () => {
        this.userPaused = !this.userPaused;
        this.classList.toggle("is-user-paused", this.userPaused);
        this.toggle.setAttribute("aria-label", this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
        if (this.userPaused) this.pause();
        else this.play();
      });
    }

    disconnectedCallback() {
      this.pause();
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    bindControls() {
      const prev = $('[data-prev]', this);
      const next = $('[data-next]', this);
      if (prev) prev.addEventListener('click', () => this.step(-1, true));
      if (next) next.addEventListener('click', () => this.step(1, true));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.go(Number(dot.dataset.dot), true));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { this.step(-1, true); }
        else if (event.key === 'ArrowRight') { this.step(1, true); }
      });

      // Pause while someone is reading or about to click.
      ['pointerenter', 'focusin'].forEach((type) => this.addEventListener(type, () => this.pause()));
      ['pointerleave', 'focusout'].forEach((type) =>
        this.addEventListener(type, () => {
          if (this.contains(document.activeElement)) return;
          if (this.canAutoplay && this.inView) this.play();
        })
      );
    }

    bindPointer() {
      if (!this.media) return;

      if (this.cursor && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.media.addEventListener('pointerenter', () => this.media.classList.add('is-hovered'));
        this.media.addEventListener('pointerleave', () => this.media.classList.remove('is-hovered'));
        this.media.addEventListener('pointermove', (event) => {
          const rect = this.media.getBoundingClientRect();
          this.cursor.style.setProperty('--x', `${Math.round(event.clientX - rect.left)}px`);
          this.cursor.style.setProperty('--y', `${Math.round(event.clientY - rect.top)}px`);
        });
      }

      let startX = null;
      let startY = null;
      this.media.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });
      this.media.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          event.preventDefault();
          this.step(dx < 0 ? 1 : -1, true);
        }
        startX = null;
        startY = null;
      });
    }

    bindVisibility() {
      this.inView = true;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            if (!this.canAutoplay) return;
            if (entry.isIntersecting) this.play();
            else this.pause();
          },
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }
      this.onVisibility = () => {
        if (document.hidden) this.pause();
        else if (this.canAutoplay && this.inView) this.play();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    step(direction, fromUser) {
      this.go((this.index + direction + this.count) % this.count, fromUser);
    }

    go(index, fromUser) {
      if (index === this.index || index < 0 || index >= this.count) return;
      this.index = index;

      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('tabindex', active ? '0' : '-1');
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });

      this.items.forEach((item, i) => {
        const active = i === index;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (fromUser && this.canAutoplay) this.play();
      else if (this.playing) this.restartProgress();
    }

    restartProgress() {
      if (!this.progress) return;
      this.progress.style.animation = 'none';
      void this.progress.offsetWidth;
      this.progress.style.animation = '';
    }

    play() {
      if (!this.canAutoplay || this.userPaused) return;
      this.pause();
      this.playing = true;
      this.classList.add('is-playing');
      this.restartProgress();
      this.timer = setInterval(() => this.step(1), this.interval);
    }

    pause() {
      this.playing = false;
      this.classList.remove('is-playing');
      clearInterval(this.timer);
    }
  }
  define('new-arrivals', NewArrivals);

  /* ------------------------------------------------------------------------
     Lightbox (lookbook + product images)
     ------------------------------------------------------------------------ */
  const ICONS = {
    close: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m5 5 14 14M19 5 5 19"/></svg>',
    prev: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M21 12H4M10 6l-6 6 6 6"/></svg>',
    next: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M3 12h17M14 6l6 6-6 6"/></svg>',
  };

  const Lightbox = {
    build() {
      if (this.dialog) return;
      const dialog = document.createElement('dialog');
      dialog.className = 'drawer lbx';
      dialog.setAttribute('aria-label', 'Image viewer');
      dialog.innerHTML = `
        <div class="lbx__top">
          <span class="lbx__count" aria-live="polite"></span>
          <button type="button" class="drawer__close" data-close aria-label="Close">${ICONS.close}</button>
        </div>
        <div class="lbx__stage"><img class="lbx__img" alt="" decoding="async"></div>
        <button type="button" class="round-btn lbx__nav lbx__prev" aria-label="Previous">${ICONS.prev}</button>
        <button type="button" class="round-btn lbx__nav lbx__next" aria-label="Next">${ICONS.next}</button>`;
      document.body.appendChild(dialog);

      this.dialog = dialog;
      this.stage = $('.lbx__stage', dialog);
      this.image = $('.lbx__img', dialog);
      this.count = $('.lbx__count', dialog);

      $('.lbx__prev', dialog).addEventListener('click', () => this.step(-1));
      $('.lbx__next', dialog).addEventListener('click', () => this.step(1));
      this.stage.addEventListener('click', (event) => {
        if (event.target === this.stage) Dialog.close(dialog);
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });

      let startX = null;
      this.stage.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true });
      this.stage.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
        startX = null;
      });
    },

    open(items, index, opener) {
      if (!items.length) return;
      this.build();
      this.items = items;
      const single = items.length < 2;
      $$('.lbx__nav', this.dialog).forEach((button) => { button.hidden = single; });
      this.show(index);
      Dialog.open(this.dialog, opener);
    },

    step(direction) {
      if (!this.items || this.items.length < 2) return;
      this.show((this.index + direction + this.items.length) % this.items.length);
    },

    show(index) {
      this.index = index;
      const item = this.items[index];
      const thumb = $('img', item);
      const source = item.getAttribute('href');
      const alt = thumb ? thumb.alt : '';

      this.image.classList.add('is-swapping');
      this.image.src = source;
      this.image.alt = alt;
      const settle = () => {
        if (this.index !== index) return;
        this.image.classList.remove('is-swapping');
      };
      if (typeof this.image.decode === 'function') this.image.decode().then(settle, settle);
      else this.image.onload = settle;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(settle, 1200);

      const template = theme.strings.imageOf || 'Image [index] of [count]';
      this.count.textContent = template.replace('[index]', index + 1).replace('[count]', this.items.length);

      [1, -1].forEach((offset) => {
        const neighbour = this.items[(index + offset + this.items.length) % this.items.length];
        if (neighbour && neighbour !== item) new Image().src = neighbour.getAttribute('href');
      });
    },
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lightbox]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const group = trigger.closest('[data-lightbox-group]');
    if (!group) return;
    event.preventDefault();
    const name = group.dataset.lightboxGroup;
    const containers = name ? $$(`[data-lightbox-group="${CSS.escape(name)}"]`) : [group];
    const items = containers.flatMap((container) => $$('[data-lightbox]', container));
    Lightbox.open(items, Math.max(0, items.indexOf(trigger)), trigger);
  });

  /* ------------------------------------------------------------------------
     Lookbook chapter tabs (scroll spy)
     ------------------------------------------------------------------------ */
  class LookbookTabs extends HTMLElement {
    connectedCallback() {
      this.links = $$('[data-target]', this);
      const chapters = this.links.map((link) => document.getElementById(link.dataset.target)).filter(Boolean);
      if (!chapters.length) return;

      this.links.forEach((link) => {
        link.addEventListener('click', (event) => {
          const chapter = document.getElementById(link.dataset.target);
          if (!chapter) return;
          event.preventDefault();
          const goingUp = chapter.getBoundingClientRect().top < 0;
          const headerHeight = goingUp ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 0 : 0;
          const top = chapter.getBoundingClientRect().top + window.scrollY - this.offsetHeight - headerHeight + 1;
          window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
          this.setActive(link.dataset.target);
          window.history.replaceState(window.history.state, '', `#${link.dataset.target}`);
        });
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.setActive(entry.target.id);
          });
        },
        { rootMargin: '-40% 0px -55% 0px' }
      );
      chapters.forEach((chapter) => this.observer.observe(chapter));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    setActive(id) {
      this.links.forEach((link) => {
        const active = link.dataset.target === id;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }
  }
  define('lookbook-tabs', LookbookTabs);

  /* ------------------------------------------------------------------------
     Newsletter popup
     ------------------------------------------------------------------------ */
  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = $('dialog', this);
      if (!this.dialog) return;
      this.dismissKey = 'plessa:popup-dismissed';
      this.subscribedKey = 'plessa:popup-subscribed';

      const posted = $('[data-popup-success], [data-popup-error]', this);
      if (posted && window.location.hash === '#PopupNewsletter') {
        if ($('[data-popup-success]', this)) storage.set(this.subscribedKey, String(Date.now()));
        this.show(0);
        return;
      }

      if (this.dataset.enabled !== 'true' || designMode) return;
      if (storage.get(this.subscribedKey)) return;
      const dismissedAt = Number(storage.get(this.dismissKey) || 0);
      const days = Number(this.dataset.days || 14);
      if (dismissedAt && Date.now() - dismissedAt < days * 86400000) return;

      this.dialog.addEventListener('dialog:close', () => storage.set(this.dismissKey, String(Date.now())));
      this.show(Number(this.dataset.delay || 8) * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    show(delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (openDialogs.size > 0) {
          this.show(5000);
          return;
        }
        Dialog.open(this.dialog);
      }, delay);
    }
  }
  define('newsletter-popup', NewsletterPopup);

  /* ------------------------------------------------------------------------
     Theme editor support
     ------------------------------------------------------------------------ */
  if (designMode) {
    const findDialog = (target) => (target ? $('dialog', target) : null);

    document.addEventListener('shopify:section:load', (event) => initReveal(event.target));

    document.addEventListener('shopify:section:select', (event) => {
      const section = event.target;
      if (section.classList.contains('section-popup') || section.classList.contains('section-cart-drawer')) {
        Dialog.open(findDialog(section));
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      const dialog = findDialog(event.target);
      if (dialog && dialog.open) Dialog.close(dialog, { restoreFocus: false });
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  const boot = () => {
    initReveal();
    if (revealObserver) document.documentElement.classList.add('reveal-ready');
    if (theme.cartType !== 'drawer') {
      $$('[data-open="CartDrawer"]').forEach((link) => link.removeAttribute('data-open'));
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.PlessaTheme = { Dialog, Cart, Lightbox };
})();
